import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, CreditCard, Calendar, Eye, EyeOff, Copy, Check } from 'lucide-react';
import type { GlobalCustomer, XmCardBrand, CardLimitGroup } from '@/types/database';
import { CARD_POOL_MAP, CARD_POOL_LIMITS } from '@/types/database';
import { getCardBrandColor } from '../customers/cardBrandColors';
import { getAllPaymentsForGlobalCustomer } from '@/lib/databaseApi';
import type { GlobalPaymentRecord } from '@/lib/databaseApi';
import LogoSpinner from '@/components/LogoSpinner';

interface PoolUsage {
  pool: CardLimitGroup;
  limit: number;
  charged: number;
  remaining: number;
  brands: string[];
}

const POOL_LABELS: Record<string, string> = {
  debit: 'Debit Pool',
  credit_classic: 'Credit Classic Pool',
  visa_credit_gold: 'Visa Credit Gold',
  master_credit_gold: 'Master Credit Gold',
  cbm_credit: 'CBM Credit',
};

interface Props {
  customer: GlobalCustomer;
  onClose: () => void;
  onEdit: () => void;
}

export default function CustomerDetailModal({ customer, onClose, onEdit }: Props) {
  const [payments, setPayments] = useState<GlobalPaymentRecord[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [revealedCards, setRevealedCards] = useState<Set<string>>(new Set());
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    getAllPaymentsForGlobalCustomer(customer.name)
      .then(setPayments)
      .catch(() => {})
      .finally(() => setLoadingPayments(false));
  }, [customer.name]);

  function toggleReveal(cardType: string) {
    setRevealedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardType)) next.delete(cardType);
      else next.add(cardType);
      return next;
    });
  }

  function maskNumber(num: string): string {
    if (num.length <= 4) return num;
    return '**** **** **** ' + num.slice(-4);
  }

  function formatCardNumber(num: string): string {
    return num.replace(/(.{4})/g, '$1 ').trim();
  }

  async function copyToClipboard(text: string, key: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 1500);
    } catch { /* ignore */ }
  }

  function computePoolUsage(): PoolUsage[] {
    const poolMap = new Map<CardLimitGroup, {
      limit: number;
      charged: number;
      brands: Set<string>;
    }>();

    for (const card of customer.owned_cards) {
      if (!poolMap.has(card.pool)) {
        poolMap.set(card.pool, {
          limit: CARD_POOL_LIMITS[card.pool],
          charged: 0,
          brands: new Set(),
        });
      }
      poolMap.get(card.pool)!.brands.add(card.type);
    }

    for (const p of payments) {
      const pool = CARD_POOL_MAP[p.card_brand as XmCardBrand];
      if (!pool || !poolMap.has(pool)) continue;
      poolMap.get(pool)!.charged += p.charged_amount;
    }

    return Array.from(poolMap.entries()).map(([pool, data]) => ({
      pool,
      limit: data.limit,
      charged: data.charged,
      remaining: data.limit - data.charged,
      brands: Array.from(data.brands),
    }));
  }

  const pools = computePoolUsage();
  const totalCharged = pools.reduce((s, p) => s + p.charged, 0);
  const totalLimit = pools.reduce((s, p) => s + p.limit, 0);
  const hasCardInfo = customer.owned_cards.some(c => c.card_number || c.expiry_date || c.cvv);

  return createPortal(
    <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-[100] p-4" onClick={onClose}>
      <div className="glass-modal rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-white">{customer.name}</h2>
            {customer.email && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <Mail size={11} className="text-white" />
                <span className="text-xs text-white">{customer.email}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { onClose(); onEdit(); }}
              className="px-3 py-1.5 text-[11px] font-medium text-teal-400 bg-teal-500/10 glass-hover-teal rounded-lg"
            >
              Edit
            </button>
            <button
              onClick={onClose}
              className="text-white hover:text-white p-1 glass-hover rounded-lg"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-5">
          <div>
            <p className="text-[10px] font-semibold text-white uppercase tracking-wider mb-3">Owned Cards</p>
            <div className="space-y-2">
              {customer.owned_cards.map(card => {
                const color = getCardBrandColor(card.type);
                const isRevealed = revealedCards.has(card.type);
                const hasInfo = card.card_number || card.expiry_date || card.cvv;
                return (
                  <div key={card.type} className="glass-inner rounded-xl p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-2.5 h-2.5 rounded-full ${color.dot}`} />
                        <span className="text-xs font-semibold text-white">{card.type}</span>
                      </div>
                      <span className="text-[10px] text-white">${card.limit_usd.toLocaleString()} limit</span>
                    </div>

                    {hasInfo && (
                      <div className="mt-2 pt-2 border-t border-white/10/60">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] text-white uppercase tracking-wider font-semibold">Card Details</span>
                          <button
                            onClick={() => toggleReveal(card.type)}
                            className="flex items-center gap-1 text-[10px] text-white hover:text-white transition-colors"
                          >
                            {isRevealed ? <EyeOff size={10} /> : <Eye size={10} />}
                            {isRevealed ? 'Hide' : 'Show'}
                          </button>
                        </div>

                        {card.card_number && (
                          <div className="flex items-center justify-between py-1">
                            <div className="flex items-center gap-1.5">
                              <CreditCard size={11} className="text-white" />
                              <span className="text-xs text-white font-mono tracking-wider">
                                {isRevealed ? formatCardNumber(card.card_number) : maskNumber(card.card_number)}
                              </span>
                            </div>
                            {isRevealed && (
                              <button
                                onClick={() => copyToClipboard(card.card_number!, `num-${card.type}`)}
                                className="p-1 text-white hover:text-teal-500 transition-colors"
                              >
                                {copiedField === `num-${card.type}` ? <Check size={11} className="text-teal-500" /> : <Copy size={11} />}
                              </button>
                            )}
                          </div>
                        )}

                        <div className="flex gap-4">
                          {card.expiry_date && (
                            <div className="flex items-center gap-1.5 py-1">
                              <Calendar size={10} className="text-white" />
                              <span className="text-xs text-white font-mono">
                                {isRevealed ? card.expiry_date : '**/**'}
                              </span>
                            </div>
                          )}
                          {card.cvv && (
                            <div className="flex items-center gap-1.5 py-1">
                              <span className="text-[10px] text-white font-semibold">CVV</span>
                              <span className="text-xs text-white font-mono">
                                {isRevealed ? card.cvv : '***'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {!hasCardInfo && customer.owned_cards.length > 0 && (
            <div className="bg-amber-500/10 rounded-xl p-3">
              <p className="text-[10px] text-amber-400">No card details have been added yet. Edit this customer to add card numbers.</p>
            </div>
          )}

          <div>
            <p className="text-[10px] font-semibold text-white uppercase tracking-wider mb-3">Usage Summary</p>
            {pools.length === 0 ? (
              <p className="text-xs text-white italic">No cards assigned</p>
            ) : (
              <div className="space-y-3">
                {pools.map(pool => {
                  const pct = pool.limit > 0 ? Math.min(100, (pool.charged / pool.limit) * 100) : 0;
                  const barColor = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-teal-500';
                  return (
                    <div key={pool.pool} className="glass-inner rounded-xl p-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className="flex -space-x-1">
                          {pool.brands.map(b => {
                            const c = getCardBrandColor(b);
                            return <div key={b} className={`w-2 h-2 rounded-full ring-1 ring-white ${c.dot}`} />;
                          })}
                        </div>
                        <span className="text-[10px] font-semibold text-white uppercase tracking-wider">
                          {POOL_LABELS[pool.pool] || pool.pool}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-1 text-center mb-2">
                        <div>
                          <p className="text-[10px] text-white">Limit</p>
                          <p className="text-xs font-bold text-white">${pool.limit.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-white">Charged</p>
                          <p className="text-xs font-bold text-white">${pool.charged.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-[10px] text-white">Remaining</p>
                          <p className={`text-xs font-bold ${pool.remaining >= 0 ? 'text-emerald-400' : 'text-red-600'}`}>
                            ${pool.remaining.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <div className="bg-white/15/50 rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}

                <div className="flex items-center justify-between px-1 pt-1">
                  <span className="text-[11px] text-white">Total Charged</span>
                  <span className="text-xs font-bold text-white">${totalCharged.toFixed(2)} / ${totalLimit.toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="text-[10px] font-semibold text-white uppercase tracking-wider mb-3">
              Recent Payments {!loadingPayments && `(${payments.length})`}
            </p>
            {loadingPayments ? (
              <div className="flex justify-center py-6">
                <LogoSpinner size={32} />
              </div>
            ) : payments.length === 0 ? (
              <p className="text-xs text-white italic">No payments recorded</p>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-auto">
                {payments.slice(0, 50).map((p, i) => {
                  const color = getCardBrandColor(p.card_brand);
                  return (
                    <div key={i} className="flex items-center justify-between px-3 py-2 glass-inner rounded-lg">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`w-2 h-2 rounded-full shrink-0 ${color.dot}`} />
                        <div className="min-w-0">
                          <p className="text-xs text-white truncate">{p.business_name}</p>
                          <p className="text-[10px] text-white">{p.card_brand} &middot; {p.month_year}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="text-xs font-semibold text-white">${p.charged_amount.toFixed(2)}</p>
                        <p className="text-[10px] text-white">{p.payment_date}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-[10px] text-white pt-2 border-t border-white/10">
            <span>Created: {new Date(customer.created_at).toLocaleDateString()}</span>
            <span>Updated: {new Date(customer.updated_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
